// MP-OS-198: a strength/format filter only finds articles whose value is known. Where a large part
// of a period's CU has no value, the filtered curve drops although sales did not, so the charts
// warn when any period in view is below COVERAGE_THRESHOLD. dos-daily and share-daily return the
// coverage per bucket (`chart.attribute_coverage`, mirrors app/api/attribute_coverage.py).
import { formatDay, formatMonth, formatPercent } from '../format'
import type { AttributeCoverage, Chart } from '../types'

export const COVERAGE_THRESHOLD = 95

type Freq = 'day' | 'week' | 'month'
const ATTRIBUTE_LABEL: Record<AttributeCoverage['attribute'], string> = { format: 'Format', strength: 'Strength' }
const UNIT: Record<Freq, string> = { day: 'days', week: 'weeks', month: 'months' }
const BUCKETS: Record<AttributeCoverage['attribute'], string> = {
  format: 'Large/Mini/Slim', strength: 'Strength 1–6',
}

export interface CoverageSource {
  label: string // which chart's scope, e.g. "Sales (CU)" or "Share market"
  chart: Chart | null | undefined
  curve?: string // MPD-133 M6: the brand curve of a per-brand chart this coverage is for
}

/**
 * MPD-133 M6: the coverage sources of the chart actually shown. A per-brand chart (mergeTrends)
 * carries the coverage per curve, so each curve is its own source ("Sales (CU) · VELO") and the
 * warning can name the curve that is below the threshold; any other chart is one source.
 */
export function chartCoverageSources(label: string, chart: Chart | null | undefined): CoverageSource[] {
  const perCurve = chart?.series_attribute_coverage
  if (!perCurve) return [{ label, chart }]
  return Object.entries(perCurve).map(([curve, entries]) => ({
    label: `${label} · ${curve}`, curve, chart: { ...chart, attribute_coverage: entries },
  }))
}

/** MPD-133 L5: a coverage percent in the ⓘ, floored, so a period below the threshold (94.6 %) never
 *  reads as the threshold ("95 %"). */
export const coveragePercentText = (pct: number): string => formatPercent(Math.floor(pct), 0)

export interface CoverageDetail {
  source: string
  periods: { period: string; label: string; pct: number }[]
  unbucketedCu: number
}

export interface CoverageWarning {
  attribute: AttributeCoverage['attribute']
  label: string // "Format"
  periods: string // "Dec 2025 – Feb 2026"
  text: string // the visible warning
  details: CoverageDetail[] // the ⓘ
}

/** A bucket name as a reader sees it: "Dec 2025", "Week 3 2026", "15 Dec 2025". */
export function periodLabel(name: string, freq: Freq): string {
  if (freq === 'month') return formatMonth(name, { short: true })
  const w = /^(\d{4})-W(\d{1,2})$/.exec(name)
  if (freq === 'week' && w) return `Week ${Number(w[2])} ${w[1]}`
  return formatDay(name)
}

const pctOf = (e: AttributeCoverage) => (e.total_cu > 0 ? (e.covered_cu / e.total_cu) * 100 : 100)

/** Consecutive low periods (in the order of all periods) as ranges: "Dec 2025 – Feb 2026, Apr 2026". */
function ranges(low: Set<string>, ordered: string[], freq: Freq): string {
  const groups: string[][] = []
  let run: string[] = []
  for (const p of ordered) {
    if (low.has(p)) run.push(p)
    else if (run.length) { groups.push(run); run = [] }
  }
  if (run.length) groups.push(run)
  return groups.map((g) => g.length === 1 ? periodLabel(g[0], freq)
    : `${periodLabel(g[0], freq)} – ${periodLabel(g[g.length - 1], freq)}`).join(', ')
}

/**
 * One warning per filtered attribute when any period in view is below the threshold in any of the
 * sources (the CU curve's scope, the share curve's market). `hidden` are bucket names not in view
 * (partial periods while they are hidden). Total CU is never touched here; this only describes it.
 */
export function coverageWarnings(sources: CoverageSource[], freq: Freq, hidden: ReadonlySet<string> = new Set()): CoverageWarning[] {
  const warnings: CoverageWarning[] = []
  for (const attribute of ['format', 'strength'] as const) {
    const per = sources.map((s) => ({
      source: s.label, curve: s.curve,
      entries: (s.chart?.attribute_coverage ?? []).filter((e) => e.attribute === attribute && !hidden.has(e.period)),
    })).filter((s) => s.entries.length)
    const ordered = [...new Set(per.flatMap((s) => s.entries.map((e) => e.period)))].sort()
    const isLow = (e: AttributeCoverage) => pctOf(e) < COVERAGE_THRESHOLD
    const low = new Set(per.flatMap((s) => s.entries.filter(isLow).map((e) => e.period)))
    if (!low.size) continue
    const label = ATTRIBUTE_LABEL[attribute]
    const periods = ranges(low, ordered, freq)
    // Per-brand mode: name the curves that are below the threshold.
    const curves = [...new Set(per.filter((s) => s.curve && s.entries.some(isLow)).map((s) => s.curve!))]
    warnings.push({
      attribute, label, periods,
      text: `${label} data incomplete for ${periods}${curves.length ? ` in ${curves.join(', ')}` : ''}: comparisons across these ${UNIT[freq]} are not reliable.`,
      details: per.map((s) => ({
        source: s.source,
        periods: s.entries.map((e) => ({ period: e.period, label: periodLabel(e.period, freq), pct: Math.floor(pctOf(e) * 10) / 10 })),
        unbucketedCu: s.entries.reduce((sum, e) => sum + e.unbucketed_cu, 0),
      })),
    })
  }
  return warnings
}

/** The ⓘ's last line: a known value outside the filter's buckets is not a missing value. */
export function unbucketedLine(attribute: AttributeCoverage['attribute'], cu: string): string {
  return `Known value outside ${BUCKETS[attribute]}: ${cu} CU (not missing, but not in any filter value).`
}

/** The trend CSV Selection block rows: ["Format coverage below 95 %", "<periods>"]. */
export function coverageSelectionRows(warnings: readonly CoverageWarning[]): [string, string][] {
  return warnings.map((w) => [`${w.label} coverage below ${COVERAGE_THRESHOLD} %`, w.periods])
}
